import { computed, ref, shallowRef } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { companiesService } from '@/services/CompaniesService'
import {
  subscriptionService,
  parseSubscriptionEffectivePayload
} from '@/services/subscriptionService'

const payload = shallowRef(null)
const loading = ref(false)

/**
 * Shared state for GET /subscription/effective: the EFFECTIVE plan/access of the
 * ACTIVE company, resolved server-side by the company owner's subscription.
 *
 * Unlike useSubscriptionCurrent (which reads the logged-in user's own billing),
 * this reflects what the active tenant is entitled to — so a worker/admin inside
 * a company they don't own inherits the owner's plan for feature-gating and the
 * sidebar plan badge. Call `refresh()` after login and on every company switch.
 *
 * Exposes the same shape useFeatureAccess relies on (planType, planDisplayName,
 * loading, payload, refresh) so it can be swapped in transparently.
 */
export function useEffectiveAccess() {
  const authStore = useAuthStore()

  const planType = computed(() => payload.value?.effectivePlanType ?? 'free')

  const planDisplayName = computed(() => {
    const p = payload.value?.plan
    if (p && typeof p === 'object') {
      if (p.displayName) return String(p.displayName)
      if (p.name) return String(p.name)
    }
    return ''
  })

  const roleName = computed(() => payload.value?.role ?? null)

  const isAccessActive = computed(() => payload.value?.isAccessActive ?? true)

  const isPromoActive = computed(() => payload.value?.isPromoActive ?? false)

  async function refresh() {
    if (!authStore.token) {
      payload.value = null
      loading.value = false
      return
    }
    // Endpoint tenantizado: sin empresa activa no hay plan efectivo que resolver.
    if (!companiesService.hasActiveCompany()) {
      payload.value = null
      loading.value = false
      return
    }
    loading.value = true
    try {
      const data = await subscriptionService.getEffectiveAccess()
      payload.value = parseSubscriptionEffectivePayload(data)
    } catch {
      payload.value = null
    } finally {
      loading.value = false
    }
  }

  function clear() {
    payload.value = null
  }

  return {
    payload,
    loading,
    planType,
    planDisplayName,
    roleName,
    isAccessActive,
    isPromoActive,
    refresh,
    clear
  }
}
